import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NodeAppBar from "./NodeAppBar";
import SectionNode from "../Nodes/SectionNode";
import { getRevAllNodesWithStatus } from "../../../../redux/procedureRevisionSlice";
import Loader from "../../../../common/Loader";
import { setTaskReference } from "../../../Tasks/Uitlities";
import { isLocked } from "../../Utils/utils";
import { pingTask } from "../../../../util/apiService";
import { showSnackbar } from "../../../../redux/snackbarSlice";
import useApiTabLock from "../../../../hooks/useApiTabLock";
import { useRevisionLock } from "../../../../contexts/RevisionLockContext";

/**
 * ViewSection Component
 * 
 * Displays section details when user checks the checkbox before section name
 * Route: /view-section/:partnumber/:revision/:partId/:revisionId/:section_id_ref
 */
const ViewSection = () => {
  const { partnumber, revision, partId, revisionId, section_id_ref } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const is_deleted = searchParams.get("is_deleted");

  const { allRevisionNodes, currentStatus } = useSelector((state) => state.procedureRevision);
  const authUser = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [pingInterval, setPingInterval] = useState(null);
  
  // State to track if we should temporarily bypass tab order lock
  // Initialize by checking if THIS specific tab has a bypass flag
  const [bypassTabOrderLock, setBypassTabOrderLock] = useState(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const bypassFlag = sessionStorage.getItem(`tabOrderLockBypass_${revisionId}_${tabId}`);
    return bypassFlag === 'true';
  });

  // Use revision ID as route key to lock entire revision across all pages
  // This ensures all pages within the same revision (Document, sections, steps, substeps, canvases, etc.)
  // share the same lock across tabs
  const routeKey = `revision_${revisionId}`;

  // Tab lock management - only one tab can perform actions
  // Use revision-specific key to ensure all pages within the same revision share the same lock
  const { isLocked: isApiLocked, isActiveTab, lockedBy } = useApiTabLock(`revision_${revisionId}`);

  // Tab order lock from provider
  const revisionLock = useRevisionLock();
  const isFirstTabByOrder =
    revisionLock?.revisionIdFromPath === revisionId ? revisionLock.isFirstTab : false;
  const isLockedByOrder =
    revisionLock?.revisionIdFromPath === revisionId ? revisionLock.isLocked : true;

  // Leadership restoration - Check if this tab was the leader before navigation
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const fromDocument = sessionStorage.getItem(`fromDocument_${revisionId}`) === 'true';
    const wasLeader = sessionStorage.getItem(`wasLeader_${revisionId}_${tabId}`) === 'true';
    
    if (fromDocument && wasLeader) {
      setBypassTabOrderLock(true);
      sessionStorage.setItem(`tabOrderLockBypass_${revisionId}_${tabId}`, 'true');
    }
  }, [revisionId]);

  // Track when this tab becomes the leader
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    if (isFirstTabByOrder) {
      sessionStorage.setItem(`wasLeader_${revisionId}_${tabId}`, 'true');
    }
  }, [isFirstTabByOrder, revisionId]);

  const isLock = isLocked(currentStatus);

  // Derive sectionData directly from Redux state
  const sectionData = allRevisionNodes?.find(
    (section) => section.tree_node_id === section_id_ref
  );

  const sectionVersionHistoryMatchIds = useMemo(
    () =>
      [
        section_id_ref,
        sectionData?.section_id_ref,
        sectionData?.detail?.section_tree_node_id,
      ].filter(Boolean),
    [
      section_id_ref,
      sectionData?.section_id_ref,
      sectionData?.detail?.section_tree_node_id,
    ],
  );

  /** Same ordering as below render so Node History matches "Section N" audit lines. */
  const sectionDisplayOrdinalForNodeHistory = useMemo(() => {
    if (!section_id_ref || !Array.isArray(allRevisionNodes)) return null;
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      const safeA = Number.isFinite(intA) ? intA : 0;
      const safeB = Number.isFinite(intB) ? intB : 0;
      return safeA - safeB;
    };
    const getSectionSortOrderRaw = (s) =>
      s?.sibling_order ??
      s?.detail?.sibling_order ??
      s?.procedure_tree?.sibling_order;

    const sortedSections = [...allRevisionNodes].sort((a, b) => {
      const orderA = getSectionSortOrderRaw(a);
      const orderB = getSectionSortOrderRaw(b);
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    const idx = sortedSections.findIndex(
      (section) => section.tree_node_id === section_id_ref,
    );
    return idx >= 0 ? idx + 1 : null;
  }, [allRevisionNodes, section_id_ref]);

  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoading(true);
        await dispatch(getRevAllNodesWithStatus({
          partId: partId,
          revisionId: revisionId,
          is_deleted: is_deleted
        }));
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
      } finally {
        setLoading(false);
      }
    };

    if (partId && revisionId && section_id_ref) {
      fetchNodes();
    }
  }, [partId, revisionId, section_id_ref, dispatch, is_deleted]);

  // Set task reference for this section - only when data changes
  // Must be called before any early returns (React Hooks rule)
  useEffect(() => {
    if (sectionData && partnumber) {
      setTaskReference(
        dispatch,
        // `Section ${sectionIndex + 1}, ${partnumber} Rev ${revision}`,
        `Section ${partnumber}`,
        sectionData?.detail?.section_tree_node_id,
        sectionData?.tree_node_id,
        sectionData?.ltree_path
      );
    }
  }, [sectionData, partnumber, dispatch]);

  const onNavigate = async (nodeType, nodeData, context) => {
    // Navigation within section - no lock API call needed
    // Start ping interval to keep session alive
    if (authUser && sectionData) {
      startPingInterval();
    }
  };

  // Start ping interval function
  const startPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
    }

    const interval = setInterval(async () => {
      try {
        if (authUser) {
          const pingData = {
            task_id: revisionId,
            user_id: authUser.id
          };
          await pingTask(pingData);
        }
      } catch (error) {
        console.error('Ping failed:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    setPingInterval(interval);
  };

  // Stop ping interval function
  const stopPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      setPingInterval(null);
    }
  };

  // Cleanup ping interval on component unmount
  useEffect(() => {
    return () => {
      stopPingInterval();
    };
  }, [pingInterval]);

  if (loading) {
    return <Loader text="Loading section details..." />;
  }

  if (!sectionData) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Section not found.</p>
      </div>
    );
  }

  // Calculate section index from data
  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    const safeA = Number.isFinite(intA) ? intA : 0;
    const safeB = Number.isFinite(intB) ? intB : 0;
    return safeA - safeB;
  };

  const getSectionSortOrderRaw = (s) =>
    s?.sibling_order ??
    s?.detail?.sibling_order ??
    s?.procedure_tree?.sibling_order;

  const sortedSections = [...(allRevisionNodes || [])].sort((a, b) => {
    const orderA = getSectionSortOrderRaw(a);
    const orderB = getSectionSortOrderRaw(b);
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  const sectionIndex = sortedSections.findIndex(
    (section) => section.tree_node_id === section_id_ref
  );

  const globalStepStart = sortedSections.slice(0, sectionIndex).reduce(
    (total, section) => total + (section.steps?.length || 0), 0
  );

  const sectionHeading = `Section ${sectionData.is_deleted ? "(Deleted)" : sectionIndex + 1}`;

  // Handle Add dropdown navigation with section_id in query param
  const handleAddElement = (type) => {
    if (type === "Section") {
      navigate(`/add-new-section/${partnumber}/${revision}/${partId}/${revisionId}?section_id=${section_id_ref}`);
    } else if (type === "Step") {
      navigate(`/add-new-step/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}`);
    }
  };

  // Handle Edit button click
  const handleEditSection = () => {
    navigate(`/edit-section/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}`);
  };
  return (
    <>
      <NodeAppBar
        title={sectionHeading}
        isSection={true}
        isSelectedPage={true}
        onAddElement={handleAddElement}
        onEditSection={handleEditSection}
        partNumber={partnumber}
        revision={revision}
        partId={partId}
        revisionId={revisionId}
        sectionId={section_id_ref}
        sectionIndex={sectionIndex}
        ltree_path={sectionData?.ltree_path}
        is_deleted={sectionData?.is_deleted || false}
        isLocked={isLock || (!bypassTabOrderLock && !isFirstTabByOrder) || (isApiLocked && lockedBy)}
        versionHistoryFallbackVersionId={
          sectionData?.detail?.version_id_ref ||
          sectionData?.version_id_ref
        }
        versionHistoryNodeId={section_id_ref}
        versionHistoryNodeIdField="section_tree_node_id"
        versionHistoryMatchIds={sectionVersionHistoryMatchIds}
        versionHistorySectionOrdinal={sectionDisplayOrdinalForNodeHistory}
      />

      <div style={{ padding: "20px" }}>
        {/* Render the section with its nodes */}
        <SectionNode
          section={sectionData}
          sectionIndex={sectionIndex}
          globalStepStart={globalStepStart}
          onNavigate={onNavigate}
          checkboxState={{
            currentNodeId: section_id_ref,
            currentNodeType: 'section',
            allChildrenChecked: true,
            allChildrenDisabled: true,
          }}
          disabled={isLock || (!bypassTabOrderLock && !isFirstTabByOrder) || (isApiLocked && lockedBy)}
          allNodesData={allRevisionNodes}
        />
      </div>
    </>
  );
};

export default ViewSection;
